import React, { useState, ChangeEvent, ClipboardEvent } from 'react';
import { IdolSize, ContentType, Mod } from '../../types/mods';
import { ITEM_TYPES } from '../../constants';
import { Idol, Stat } from '../../types';
import { mods } from '../../data/mods';
import { UNIQUE_IDOLS } from './IdolForm/constants/uniqueIdols';
import { UniqueIdol } from './IdolForm/types';

/**
 * Utility function to extract the idol size from an item name
 * @param itemName - The full item name from the pasted text
 * @returns The idol size if found, or null if not found
 */
const extractIdolSize = (itemName: string, itemRarity: string): IdolSize | null => {
  if (itemRarity === 'Unique') {
    return IdolSize.UNIQUE;
  }

  const sizePatterns: Array<{ pattern: RegExp; size: IdolSize }> = [
    { pattern: /Minor Idol/i, size: IdolSize.MINOR },
    { pattern: /Kamasan Idol/i, size: IdolSize.KAMASAN },
    { pattern: /Totemic Idol/i, size: IdolSize.TOTEMIC },
    { pattern: /Conqueror Idol/i, size: IdolSize.CONQUEROR },
    { pattern: /Noble Idol/i, size: IdolSize.NOBLE },
    { pattern: /Burial Idol/i, size: IdolSize.BURIAL },
  ];

  for (const { pattern, size } of sizePatterns) {
    if (pattern.test(itemName)) {
      return size;
    }
  }

  return null;
};

/**
 * Extract numeric values from a mod text
 * @param modText - The explicit mod text from the pasted item
 * @returns Array of numeric values extracted from the mod text
 */
const extractNumericValues = (modText: string): number[] => {
  const matches = modText.match(/([+-]?\d+(?:\.\d+)?)/g);
  if (!matches) return [];
  return matches.map(value => parseFloat(value));
};

/**
 * Tokenizes a mod description for better matching
 * @param description - The mod description to tokenize
 * @returns Object containing tokens and other matching information
 */
const tokenizeMod = (description: string): {
  contentType: string | null;
  subject: string | null;
  action: string | null;
  normalized: string;
  segments: string[];
  numericPositions: number[];
} => {
  // Normalize the text first
  const normalized = description
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

  // Find positions of all numbers to help with segmentation
  const numericMatches = Array.from(normalized.matchAll(/[+-]?\d+(?:\.\d+)?%?|\([^)]+\)%?/g));
  const numericPositions = numericMatches.map(match => match.index || 0);

  // Identify the content type
  const contentTypes = [
    "abyss", "delirium", "breach", "betrayal", "harvest", "ritual", "legion",
    "expedition", "essence", "blight", "heist", "incursion", "beyond", "delve",
    "harbinger", "ultimatum", "anarchy", "bestiary", "boss", "shrine", "map",
    "torment", "scarab", "ambush", "vaal"
  ];

  let contentType: string | null = null;
  for (const type of contentTypes) {
    if (normalized.includes(type)) {
      contentType = type;
      break;
    }
  }

  // Extract the subject (what's being modified) - typically at the start of the mod
  // Examples: "Your Maps", "Abyss Cracks", "Legion Encounters"
  let subject: string | null = null;

  // Common subject patterns
  const subjectPatterns = [
    /^(your maps)(?= have| contain)/i,
    /^(.*?(?:cracks|troves|spires|monsters|altars|encounters|bosses|spirits|chests|veins|shrines))(?= in your maps| have)/i,
  ];

  for (const pattern of subjectPatterns) {
    const match = normalized.match(pattern);
    if (match && match[1]) {
      subject = match[1].trim();
      break;
    }
  }

  // Try to identify the main action of the mod
  // Like "have chance to contain", "increased", "have chance to spawn", etc.
  let action: string | null = null;

  const actionPatterns = [
    /have (?:\+|\(.*?\))?\s?(?:\d+(?:\.\d+)?%?)?\s?chance to contain/i,
    /have (?:\+|\(.*?\))?\s?(?:\d+(?:\.\d+)?%?)?\s?chance to spawn/i,
    /have (?:\+|\(.*?\))?\s?(?:\d+(?:\.\d+)?%?)?\s?increased chance/i,
    /(?:\d+(?:\.\d+)?%?)?\s?increased/i,
    /(?:\d+(?:\.\d+)?%?)?\s?more/i,
    /(?:\d+(?:\.\d+)?%?)?\s?chance to/i,
    /contain an additional/i,
  ];

  for (const pattern of actionPatterns) {
    const match = normalized.match(pattern);
    if (match) {
      action = match[0].trim();
      break;
    }
  }

  // Break the mod into meaningful segments
  // This helps compare the overall structure of the mod
  const segments = normalized
    // Replace numbers with placeholders to focus on the text structure
    .replace(/\+?(\d+(?:\.\d+)?)%?/g, 'NUMBER')
    .replace(/\(\d+(?:\.\d+)?–\d+(?:\.\d+)?\)%?/g, 'RANGE')
    // Split on common delimiters keeping the structure
    .split(/\b(in your maps|have|chance to|increased|more|additional)\b/)
    .map(s => s.trim())
    .filter(s => s.length > 0);

  return {
    contentType,
    subject,
    action,
    normalized,
    segments,
    numericPositions
  };
};

const findUniqueIdolByName = (uniqueIdolName: string) => {
  return UNIQUE_IDOLS.find((idol: UniqueIdol) => idol.name === uniqueIdolName) || null;
};

/**
 * Match result interface
 */
interface MatchingModResult {
  mod: Mod;
  value: number | null;
  score: number;
}

/**
 * Improved function to find matching mods based on semantic understanding
 * @param modText - The mod text from the pasted item
 * @param idolSize - The idol size
 * @returns The matching mod result or null if no match found
 */
const findMatchingMod = (modText: string, idolSize: IdolSize): MatchingModResult | null => {
  // Extract numeric values
  const numericValues = extractNumericValues(modText);

  // Create tokens for the input mod
  const inputTokens = tokenizeMod(modText);

  // Get all mods for this idol size
  const potentialMods = Object.values(mods).filter(mod => mod.idolSizes.includes(idolSize));

  if (potentialMods.length === 0) {
    console.warn(`No mods found for idol size ${idolSize}`);
    return null;
  }

  // Score each potential mod by similarity
  const scoredMods = potentialMods.map(mod => {
    const dbTokens = tokenizeMod(mod.description);

    // Start with a base score
    let score = 0;

    // Content type match is a strong signal
    if (inputTokens.contentType && inputTokens.contentType === dbTokens.contentType) {
      score += 30;
    }

    // Subject match is also very important
    if (inputTokens.subject && inputTokens.subject === dbTokens.subject) {
      score += 30;
    }

    // Action match helps differentiate similar mods
    if (inputTokens.action && dbTokens.action &&
      (inputTokens.action.includes(dbTokens.action) ||
        dbTokens.action.includes(inputTokens.action))) {
      score += 20;
    }

    // Compare the segments for structural similarity
    const segmentScore = calculateSegmentSimilarity(inputTokens.segments, dbTokens.segments);
    score += segmentScore * 20;

    // Full text similarity as a fallback
    const textSimilarity = calculateTextSimilarity(inputTokens.normalized, dbTokens.normalized);
    score += textSimilarity * 10;

    // Check if words after the numeric parts are similar
    // This helps differentiate mods with similar beginnings but different effects
    const afterNumericSimilarity = compareAfterNumeric(
      inputTokens.normalized,
      dbTokens.normalized,
      inputTokens.numericPositions,
      dbTokens.numericPositions
    );
    score += afterNumericSimilarity * 20;

    // If the content types and subjects match exactly, this is probably the right mod
    // Check for exact match after normalizing the numbers
    const strippedInput = stripNumbers(inputTokens.normalized);
    const strippedDb = stripNumbers(dbTokens.normalized);

    if (strippedInput === strippedDb) {
      score += 50; // Strong bonus for exact match after stripping numbers
    }

    // Special handling for numeric values in specific contexts
    const inputNumerics = extractNumericContext(inputTokens.normalized);
    const dbNumerics = extractNumericContext(dbTokens.normalized);

    // Compare numbers in similar contexts
    for (const inputNum of inputNumerics) {
      // Skip the first number as it's usually the variable value of the mod
      if (inputNum.position === inputTokens.numericPositions[0]) continue;

      // Look for DB numbers with similar context
      for (const dbNum of dbNumerics) {
        // If contexts are similar but numbers differ, penalize the score
        if (calculateTextSimilarity(inputNum.context, dbNum.context) > 0.6 &&
          inputNum.number !== dbNum.number) {
          score -= 40; // Strong penalty for mismatched numbers in similar contexts
        }
      }
    }

    return {
      mod,
      score,
      value: numericValues.length > 0 ? numericValues[0] : null
    };
  });

  // Sort by score descending
  scoredMods.sort((a, b) => b.score - a.score);

  // Accept a match if it scores high enough or is significantly better than alternatives
  if (scoredMods.length > 0) {
    const bestMatch = scoredMods[0];

    // Accept match if:
    // 1. It's a very strong match (score > 70), or
    // 2. It's the best match by far (score > 40 and significantly better than second best)
    const secondBestScore = scoredMods.length > 1 ? scoredMods[1].score : 0;

    if (bestMatch.score > 60 ||
      (bestMatch.score > 40 && bestMatch.score > secondBestScore * 1.15)) {
      return bestMatch;
    }
  }

  console.warn(`No confident match found for: ${modText}`);
  return null;
};

/**
 * Strip all numeric values from a string to compare the text structure
 */
const stripNumbers = (text: string): string => {
  return text
    .replace(/\+?(\d+(?:\.\d+)?)%?/g, 'NUMBER')
    .replace(/\(\d+(?:\.\d+)?–\d+(?:\.\d+)?\)%?/g, 'RANGE')
    .replace(/\s+/g, ' ')
    .trim();
};

/**
 * Compare text following numeric values in mod descriptions
 * This helps distinguish mods with similar beginnings but different effects
 */
const compareAfterNumeric = (
  input: string,
  db: string,
  inputPositions: number[],
  dbPositions: number[]
): number => {
  if (inputPositions.length === 0 || dbPositions.length === 0) {
    return 0;
  }

  try {
    // Get text after the first numeric value
    const inputAfter = input.slice(inputPositions[0] + 1).trim();
    const dbAfter = db.slice(dbPositions[0] + 1).trim();

    // Calculate word-level Jaccard similarity
    const inputWords = inputAfter.split(/\s+/).filter(w => w.length > 3);
    const dbWords = dbAfter.split(/\s+/).filter(w => w.length > 3);

    if (inputWords.length === 0 || dbWords.length === 0) {
      return 0;
    }

    const commonWords = inputWords.filter(w => dbWords.includes(w));
    return commonWords.length / (inputWords.length + dbWords.length - commonWords.length);
  } catch (error) {
    console.error("Error in compareAfterNumeric:", error);
    return 0;
  }
};

/**
 * Calculate similarity between two arrays of segments
 * @param segments1 - First array of segments
 * @param segments2 - Second array of segments
 * @returns Similarity score (0-1)
 */
const calculateSegmentSimilarity = (segments1: string[], segments2: string[]): number => {
  if (segments1.length === 0 || segments2.length === 0) {
    return 0;
  }

  // Check for exact segment match first
  if (segments1.join('|') === segments2.join('|')) {
    return 1.0;
  }

  // Count matching segments
  let matches = 0;
  for (const seg1 of segments1) {
    if (segments2.includes(seg1)) {
      matches++;
    }
  }

  // Jaccard similarity
  return matches / (segments1.length + segments2.length - matches);
};

/**
 * Calculate text similarity using Jaccard similarity of words
 * @param text1 - First text
 * @param text2 - Second text
 * @returns Similarity score (0-1)
 */
const calculateTextSimilarity = (text1: string, text2: string): number => {
  const words1 = text1.split(/\s+/).filter(w => w.length > 3);
  const words2 = text2.split(/\s+/).filter(w => w.length > 3);

  if (words1.length === 0 || words2.length === 0) {
    return 0;
  }

  const commonWords = words1.filter(w => words2.includes(w));
  return commonWords.length / (words1.length + words2.length - commonWords.length);
};

/**
 * Extracts all numeric values with their positions and surrounding context
 * This helps distinguish between similar mods with different numeric values in specific positions
 */
const extractNumericContext = (text: string): { number: number; context: string; position: number }[] => {
  const result: { number: number; context: string; position: number }[] = [];

  // Find all numbers in the text
  const matches = Array.from(text.matchAll(/(\d+(?:\.\d+)?)/g));

  for (const match of matches) {
    if (match.index !== undefined) {
      // Get surrounding context (5 chars before and after)
      const start = Math.max(0, match.index - 5);
      const end = Math.min(text.length, match.index + match[0].length + 5);
      const context = text.substring(start, end);

      result.push({
        number: parseFloat(match[0]),
        context,
        position: match.index
      });
    }
  }

  return result;
};

// In the processUniqueIdol function
/**
 * Process a unique idol and generate stats from its description
 * @param uniqueIdolName - The name of the unique idol
 * @param modTexts - The mod texts from the pasted item
 * @returns Array of stats for the unique idol
 */
const processUniqueIdol = (uniqueIdolName: string, modTexts: string[]): Stat[] => {
  const uniqueIdol = findUniqueIdolByName(uniqueIdolName);
  if (!uniqueIdol) {
    console.error(`Could not find unique idol with name: "${uniqueIdolName}"`);
    return [];
  }

  const stats: Stat[] = [];

  // Create an ID-to-mod mapping for easier lookup with proper typing
  const modById: Record<number, Mod> = {};
  Object.values(mods).forEach(mod => {
    modById[mod.id] = mod;
  });

  // Special handling for Twisted Wish at the start of processUniqueIdol
  if (uniqueIdolName === "Twisted Wish") {
    // First add the fixed modifiers
    if (uniqueIdol.fixedModifiers) {
      uniqueIdol.fixedModifiers.forEach(modifier => {
        const modId = modifier.modId;
        stats.push({
          explicitType: "prefix",
          value: modifier.defaultValue || 0,
          contentType: ContentType.ULTIMATUM,
          modId: modId,
        });
      });
    }

    // Extract the variable mod texts based on the presence of fixed modifiers
    const variableModTexts = modTexts.filter(modText => {
      // Check if the modText corresponds to a fixed modifier
      return !uniqueIdol.fixedModifiers?.some(modifier => modText.includes(modifier.modId.toString()));
    });

    // Direct keyword-to-modId mapping
    const keywordToModId = {
      "Abyss": 1081,
      "Blight": 1082,
      "Breach": 1083,
      "Catalysts": 1084,
      "Corrupted": 1085,
      "Currency": 1086,
      "Delirium": 1087,
      "Divination": 1088,
      "Essences": 1089,
      "Fossils": 1090,
      "Map Fragments": 1091,
      "Gems": 1092,
      "Heist": 1093,
      "Inscribed Ultimatums": 1094,
      "Legion": 1095,
      "to be Maps": 1096,
      "Unique": 1097,
    };

    // Process each variable mod text
    for (const modText of variableModTexts) {

      // Extract value
      const values = extractNumericValues(modText);
      const value = values.length > 0 ? values[0] : 85;

      // Find matching keyword
      let foundModId = null;
      for (const [keyword, modId] of Object.entries(keywordToModId)) {
        if (modText.includes(keyword)) {
          foundModId = modId;
          break;
        }
      }

      // If found, add to stats
      if (foundModId) {
        stats.push({
          explicitType: "prefix",
          value: value,
          contentType: ContentType.ULTIMATUM,
          modId: foundModId,
        });
      } else {
        console.warn(`No matching keyword found for: "${modText}"`);
      }
    }

    return stats; // Return immediately
  }



  // Process fixed modifiers
  if (uniqueIdol.fixedModifiers) {
    uniqueIdol.fixedModifiers.forEach((modifier) => {
      const modId = modifier.modId;
      const modInfo = modById[modId];

      stats.push({
        explicitType: modInfo?.type === 'prefix' ? "prefix" : "suffix",
        value: modifier.defaultValue || 0,
        contentType: modInfo?.contentType || ContentType.MISC,
        modId: modId,
      });
    });
  }

  // Process fixed value modifiers
  if (uniqueIdol.fixedValueModifiers) {
    uniqueIdol.fixedValueModifiers.forEach((modifier, index) => {
      const modId = modifier.modId;
      const modInfo = modById[modId];

      // Calculate the correct index in the description array
      const startIndex = uniqueIdol.fixedModifiers ? uniqueIdol.fixedModifiers.length : 0;

      let foundValue = modifier.defaultValue;

      // Try to match with the actual mod text - only for fixed value modifiers
      if (modTexts.length > startIndex + index) {
        const modText = modTexts[startIndex + index]; // Adjusted index to match fixed modifiers

        const extractedValues = extractNumericValues(modText);
        if (extractedValues.length > 0) {
          foundValue = extractedValues[0];
        } else {
        }
      } else {
      }

      stats.push({
        explicitType: modInfo?.type === 'prefix' ? "prefix" : "suffix",
        value: foundValue,
        contentType: modInfo?.contentType || ContentType.MISC,
        modId: modId,
      });
    });
  }

  // Process variable modifiers
  if (uniqueIdol.variableModifiers && uniqueIdol.variableModifiersRequired) {
    // Skip the fixed modifiers in the mod texts
    const fixedModsCount = (uniqueIdol.fixedModifiers?.length || 0) +
      (uniqueIdol.fixedValueModifiers?.length || 0);

    const variableModTexts = modTexts.slice(fixedModsCount);

    // Create a map of all available variable modifiers by ID for easier lookup
    const variableModsById: Record<number, typeof uniqueIdol.variableModifiers[0]> = {};
    uniqueIdol.variableModifiers.forEach(mod => {
      variableModsById[mod.modId] = mod;
    });

    // For each mod text, find the best match from the variable modifiers
    const usedModIds = new Set<number>();
    const matchedMods: { modText: string; modId: number; value: number }[] = [];

    for (const modText of variableModTexts) {

      // Extract specific reward type from mod text
      const rewardTypeMatch = modText.match(/be\s+([A-Za-z\s]+)$/);
      const rewardType = rewardTypeMatch ? rewardTypeMatch[1].trim() : "";

      // Find the best matching mod ID
      let bestMatchId = -1;
      let bestMatchScore = 0;

      for (const mod of Object.values(mods)) {
        // Skip if not a variable modifier for this unique idol or already used
        if (!variableModsById[mod.id] || usedModIds.has(mod.id)) continue;

        // Check if the mod description contains the reward type
        const modRewardTypeMatch = mod.description.match(/be\s+([A-Za-z\s]+)$/);
        const modRewardType = modRewardTypeMatch ? modRewardTypeMatch[1].trim() : "";

        if (modRewardType && rewardType) {
          // Calculate similarity between reward types
          const similarity = calculateRewardTypeSimilarity(rewardType, modRewardType);

          if (similarity > bestMatchScore) {
            bestMatchScore = similarity;
            bestMatchId = mod.id;
          }
        }
      }

      // If we found a good match
      if (bestMatchId >= 0 && bestMatchScore > 0.5) {
        // Extract the numeric value
        const extractedValues = extractNumericValues(modText);
        const value = extractedValues.length > 0 ? extractedValues[0] : variableModsById[bestMatchId].defaultValue;

        matchedMods.push({
          modText: modText,
          modId: bestMatchId,
          value: value
        });

        usedModIds.add(bestMatchId);
      } else {
      }
    }

    // Add all matched mods to stats
    matchedMods.forEach(match => {
      const modInfo = modById[match.modId] || Object.values(mods).find(mod => mod.id === match.modId);

      stats.push({
        explicitType: modInfo?.type === 'prefix' ? "prefix" : "suffix",
        value: match.value,
        contentType: modInfo?.contentType || ContentType.MISC,
        modId: match.modId,
      });
    });

    // Fill in any remaining required mods
    const remainingCount = uniqueIdol.variableModifiersRequired - matchedMods.length;
    if (remainingCount > 0) {

      uniqueIdol.variableModifiers
        .filter(modifier => !usedModIds.has(modifier.modId))
        .slice(0, remainingCount)
        .forEach(modifier => {
          const modId = modifier.modId;
          const modInfo = modById[modId] || Object.values(mods).find(mod => mod.id === modId);

          stats.push({
            explicitType: modInfo?.type === 'prefix' ? "prefix" : "suffix",
            value: modifier.defaultValue,
            contentType: modInfo?.contentType || ContentType.MISC,
            modId: modId,
          });
        });
    }
  }
  return stats;
};


// Helper function to calculate similarity between reward types
function calculateRewardTypeSimilarity(type1: string, type2: string): number {
  // Direct match
  if (type1 === type2) return 1.0;

  // Check for substring match (e.g., "Legion Items" vs "Legion")
  if (type1.includes(type2) || type2.includes(type1)) return 0.9;

  // Special cases for similar reward types
  const specialCases: [string, string, number][] = [
    ["Blight Oils", "Oils", 0.9],
    ["Inscribed Ultimatums", "Ultimatums", 0.9],
    ["Legion Items", "Legion", 0.9],
    // Add more special cases as needed
  ];

  for (const [a, b, score] of specialCases) {
    if ((type1.includes(a) && type2.includes(b)) ||
      (type1.includes(b) && type2.includes(a))) {
      return score;
    }
  }

  // Fallback to word-level Jaccard similarity
  return calculateTextSimilarity(type1, type2);
}

/**
 * Main function to parse pasted idol text
 * @param pasteText - The full text pasted from the clipboard
 * @returns The parsed idol object or null if parsing failed
 */
export const parseIdolFromPaste = (pasteText: string): Idol | null => {
  // Check if this is an idol
  if (!pasteText.includes('Item Class: Idols')) {
    return null;
  }

  // Split into lines and clean them
  const lines = pasteText.split('\n').map(line => line.trim()).filter(line => line !== '');

  // Extract item name and rarity
  let itemName = '';
  let itemRarity = '';

  for (let i = 0; i < lines.length; i++) {
    if (lines[i].startsWith('Rarity:')) {
      itemRarity = lines[i].split(':')[1].trim();

      // Get the item name (either one line or two for rare items)
      if (i + 1 < lines.length && lines[i + 1] !== '--------') {
        itemName = lines[i + 1];

        // For Rare items, check if the name spans two lines
        if (itemRarity === 'Rare' && i + 2 < lines.length && lines[i + 2] !== '--------') {
          itemName += ' ' + lines[i + 2];
        }
      }
      break;
    }
  }

  // Extract idol size from the item name
  const idolSize = extractIdolSize(itemName, itemRarity);
  if (!idolSize) {
    console.error('Could not determine idol size from:', itemName);
    return null;
  }

  // Parse the sections by finding the separator lines
  const sections = [];
  let currentSection = [];

  for (let i = 0; i < lines.length; i++) {
    if (lines[i] === '--------') {
      if (currentSection.length > 0) {
        sections.push(currentSection);
        currentSection = [];
      }
    } else {
      currentSection.push(lines[i]);
    }
  }

  // Add the last section if there is one
  if (currentSection.length > 0) {
    sections.push(currentSection);
  }

  // Find the explicit mods section
  let explicitMods: string[] = [];
  let implicitsMods: string[] = [];
  let explicitSectionFound = false;

  // Skip the header (first section) and item level section (if present)
  let currentSectionIndex = 1;

  // Skip item level section if present
  if (sections.length > currentSectionIndex &&
    sections[currentSectionIndex].some(line => line.startsWith('Item Level:'))) {
    currentSectionIndex++;
  }

  // The next section should be implicit mods
  if (sections.length > currentSectionIndex) {
    implicitsMods = sections[currentSectionIndex];
    currentSectionIndex++;
  }

  // The section after implicit should be explicit mods
  if (sections.length > currentSectionIndex) {
    // Make sure this isn't the footer section
    if (!sections[currentSectionIndex].some(line =>
      line.includes('Place this item into the Idol inventory') ||
      line === 'Unmodifiable' ||
      line.includes('Item Level:') ||
      line.includes('implicit'))) {
      explicitMods = sections[currentSectionIndex];
      explicitSectionFound = true;
    }
  }

  // For unique idols, we need to also skip the "Limited to: 1" section
  if (idolSize === IdolSize.UNIQUE &&
    sections.length > currentSectionIndex &&
    sections[currentSectionIndex].some(line => line.includes('Limited to:'))) {
    currentSectionIndex++;
  }

  // If we couldn't identify explicit mods, try another approach
  if (!explicitSectionFound) {
    for (const section of sections) {
      // Skip known non-mod sections
      if (section.some(line =>
        line.startsWith('Rarity:') ||
        line.startsWith('Item Level:') ||
        line.includes('Place this item') ||
        line === 'Unmodifiable' ||
        line.includes('implicit'))) {
        continue;
      }

      // Check if this section contains likely mod text
      const potentialMods = section.filter(line =>
        line.includes('Maps') ||
        line.includes('%') ||
        Object.values(ContentType).some(contentType =>
          line.toLowerCase().includes(contentType.toLowerCase())
        )
      );

      if (potentialMods.length > 0 && section !== implicitsMods) {
        explicitMods = potentialMods;
        break;
      }
    }
  }

  // If still no explicit mods found, return null
  if (explicitMods.length === 0) {
    console.error('No explicit mods found in pasted text');
    return null;
  }

  // Match mods to database
  let stats: Stat[] = [];
  const usedModIds = new Set<number>(); // Track used mod IDs to prevent duplicates

  if (idolSize === IdolSize.UNIQUE) {
    stats = processUniqueIdol(itemName, explicitMods);
  } else {
    for (const modText of explicitMods) {
      // Skip any lines that look like section headers or metadata
      if (modText.startsWith('Rarity:') ||
        modText.startsWith('Item Level:') ||
        modText.includes('implicit') ||
        modText.includes('Place this item')) {
        continue;
      }

      // Find a matching mod
      const matchResult = findMatchingMod(modText, idolSize);

      if (matchResult) {
        const { mod, value } = matchResult;

        // Prevent duplicate mod IDs
        if (!usedModIds.has(mod.id)) {
          stats.push({
            explicitType: mod.type === 'prefix' ? "prefix" : "suffix",
            value: value !== null ? value : 0,
            contentType: mod.contentType,
            modId: mod.id,
          });

          usedModIds.add(mod.id);
        } else {
          console.warn(`Skipping duplicate mod ID ${mod.id} for "${modText}"`);

          // Try to find an alternative match
          const filteredMods = Object.values(mods).filter(m =>
            m.idolSizes.includes(idolSize) &&
            !usedModIds.has(m.id) &&
            m.type === mod.type // Try to match the same type (prefix/suffix)
          );

          if (filteredMods.length > 0) {
            // Find the best alternative match by re-running the matching algorithm
            // against only unused mods
            const altMatches = filteredMods.map(altMod => {
              const dbTokens = tokenizeMod(altMod.description);
              const inputTokens = tokenizeMod(modText);

              let score = 0;

              // Content type match
              if (inputTokens.contentType && inputTokens.contentType === dbTokens.contentType) {
                score += 30;
              }

              // Subject match
              if (inputTokens.subject && inputTokens.subject === dbTokens.subject) {
                score += 30;
              }

              // Text similarity
              const textSimilarity = calculateTextSimilarity(inputTokens.normalized, dbTokens.normalized);
              score += textSimilarity * 20;

              return {
                mod: altMod,
                score,
                value: value
              };
            });

            // Sort by score
            altMatches.sort((a, b) => b.score - a.score);

            if (altMatches.length > 0 && altMatches[0].score > 30) {
              const altMatch = altMatches[0];
              stats.push({
                explicitType: altMatch.mod.type === 'prefix' ? "prefix" : "suffix",
                value: altMatch.value !== null ? altMatch.value : 0,
                contentType: altMatch.mod.contentType,
                modId: altMatch.mod.id,
              });

              usedModIds.add(altMatch.mod.id);
            }
          }
        }
      } else {
        console.warn(`Could not match mod: "${modText}" for idol size ${idolSize}`);
      }
    }
  }

  let itemType = null;

  if (idolSize === IdolSize.UNIQUE) {
    itemType = ITEM_TYPES.UNIQUE;
  } else {
    // Get the correct item type for the idol size
    const itemTypeKey = Object.keys(ITEM_TYPES).find(key => {
      const typeName = ITEM_TYPES[key].name.toUpperCase();
      const sizeStr = idolSize.toString().toUpperCase();
      return typeName.includes(sizeStr);
    });

    itemType = itemTypeKey ? ITEM_TYPES[itemTypeKey] : null;
  }

  // Move this outside of the if/else so it's applied to both cases
  if (!itemType) {
    console.error('Could not find item type for idol size:', idolSize);
    return null;
  }

  // Create the idol object with truly unique ID
  const uniqueID = `pasted-${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${crypto.getRandomValues(new Uint32Array(1))[0].toString(16)}`;
  const idol: Idol = {
    id: uniqueID,
    name: itemName,
    size: itemType,
    stats,
  };

  return idol;
};

interface IdolPasteHandlerProps {
  onAddIdol: (idol: Idol) => void;
}

/**
 * React component for the paste handler
 */
const IdolPasteHandler: React.FC<IdolPasteHandlerProps> = ({ onAddIdol }) => {
  const [pasteInput, setPasteInput] = useState<string>('');
  const [parseResult, setParseResult] = useState<Idol | null>(null);
  const [error, setError] = useState<string>('');

  const handlePaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const text = e.clipboardData?.getData('text') || '';
    setPasteInput(text);
  };

  const handleTextChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setPasteInput(e.target.value);
  };

  const handleParse = () => {
    try {
      setError('');
      const idol = parseIdolFromPaste(pasteInput);

      if (idol) {
        setParseResult(idol);
      } else {
        setError('Failed to parse idol from text. Make sure you are pasting a valid idol item.');
      }
    } catch (err) {
      setError(`Error parsing item: ${err instanceof Error ? err.message : String(err)}`);
      console.error(err);
    }
  };

  const handleAdd = () => {
    if (parseResult) {
      onAddIdol(parseResult);
      // Clear the form
      setPasteInput('');
      setParseResult(null);
    }
  };

  const handleClear = () => {
    setPasteInput('');
    setParseResult(null);
    setError('');
  };

  return (
    <div className="bg-gray-800 p-4 rounded-lg shadow-lg">
      <h2 className="text-xl font-bold mb-4">Import Idol from Clipboard</h2>

      <div className="mb-4">
        <label className="block text-sm mb-1">Paste Idol Item Text:</label>
        <textarea
          value={pasteInput}
          onChange={handleTextChange}
          onPaste={handlePaste}
          className="w-full p-2 h-60 rounded bg-gray-700 text-white font-mono text-sm"
          placeholder="Paste your idol item text here..."
        />
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={handleParse}
          className="bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded text-white"
        >
          Parse Item
        </button>

        <button
          onClick={handleClear}
          className="bg-gray-600 hover:bg-gray-500 px-4 py-2 rounded text-white"
        >
          Clear
        </button>
      </div>

      {error && (
        <div className="bg-red-900 p-3 rounded mb-4 text-sm">
          {error}
        </div>
      )}

      {parseResult && (
        <div className="mb-4">
          <h3 className="text-lg font-semibold mb-2">Parsed Item:</h3>
          <div className="bg-gray-700 p-3 rounded">
            <p><strong>Name:</strong> {parseResult.name}</p>
            <p><strong>Type:</strong> {parseResult.size.name}</p>
            <p><strong>Stats:</strong></p>
            <ul className="list-disc pl-5">
              {parseResult.stats.map((stat, index) => (
                <li key={index}>
                  {stat.explicitType}: {mods[stat.modId]?.description.replace(/\(\d+–\d+\)/g, stat.value.toString())}
                </li>
              ))}
            </ul>

            <button
              onClick={handleAdd}
              className="mt-3 bg-green-600 hover:bg-green-500 px-4 py-2 rounded text-white"
            >
              Add to Stash
            </button>
          </div>
        </div>
      )}

      <div className="text-sm text-gray-400 mt-2">
        <p>Tip: You can press Ctrl+V with this window focused to paste your clipboard content.</p>
      </div>
    </div>
  );
};

export default IdolPasteHandler;